import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import api from '../../api/api';

// Список доступных часовых поясов
const TIMEZONES = {
  "Europe/Moscow": "Москва (UTC+3)",
  "Europe/Samara": "Самара (UTC+4)",
  "Asia/Yekaterinburg": "Екатеринбург (UTC+5)",
  "Asia/Omsk": "Омск (UTC+6)",
  "Asia/Krasnoyarsk": "Красноярск (UTC+7)",
  "Asia/Irkutsk": "Иркутск (UTC+8)",
  "Asia/Yakutsk": "Якутск (UTC+9)",
  "Asia/Vladivostok": "Владивосток (UTC+10)",
  "Asia/Magadan": "Магадан (UTC+11)",
  "Asia/Kamchatka": "Камчатка (UTC+12)",
  "Europe/Kaliningrad": "Калининград (UTC+2)",
  "Asia/Tashkent": "Ташкент (UTC+5)",
  "Asia/Almaty": "Алматы (UTC+6)",
  "Europe/Minsk": "Минск (UTC+3)",
  "Europe/Kiev": "Киев (UTC+2)"
};

const AddRentalPointDialog = ({ open, onClose, onSuccess }) => {
  const [newPoint, setNewPoint] = useState({
    name: '',
    address: '',
    phone: '',
    working_hours: '',
    description: '',
    timezone: 'Europe/Moscow'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (loading) return;
    setNewPoint({
      name: '',
      address: '',
      phone: '',
      working_hours: '',
      description: '',
      timezone: 'Europe/Moscow'
    });
    setError('');
    onClose();
  };

  const handleSave = async () => {
    try {
      // Валидация
      if (!newPoint.name.trim()) {
        setError('Название пункта аренды обязательно для заполнения');
        return;
      }

      setLoading(true);
      setError('');

      console.log('🏢 Creating new rental point:', newPoint);

      // Отправляем POST запрос для создания новой точки аренды
      const response = await api.post('/rental-points/', {
        ...newPoint,
        name: newPoint.name.trim(),
        // Убираем пустые поля или заменяем на null
        address: newPoint.address.trim() || null,
        phone: newPoint.phone.trim() || null,
        working_hours: newPoint.working_hours.trim() || null,
        description: newPoint.description.trim() || null
      });

      console.log('✅ Rental point created successfully:', response.data);

      // Очищаем форму
      setNewPoint({
        name: '',
        address: '',
        phone: '',
        working_hours: '',
        description: '',
        timezone: 'Europe/Moscow'
      });

      // Вызываем callback для обновления списка точек аренды
      if (onSuccess) {
        onSuccess(response.data);
      }

      onClose();

    } catch (error) {
      console.error('❌ Error creating rental point:', error);
      setError(error.response?.data?.detail || 'Ошибка при создании точки аренды');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        🏢 Добавить новую точку аренды
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="Название точки аренды *"
              fullWidth
              value={newPoint.name}
              onChange={(e) => setNewPoint({ ...newPoint, name: e.target.value })}
              placeholder="Например: Офис в центре города"
              required
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Адрес"
              fullWidth
              value={newPoint.address}
              onChange={(e) => setNewPoint({ ...newPoint, address: e.target.value })}
              placeholder="Например: ул. Пушкина, д. 10"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label="Телефон"
              fullWidth
              value={newPoint.phone}
              onChange={(e) => setNewPoint({ ...newPoint, phone: e.target.value })}
              placeholder="Например: +7 (999) 123-45-67"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label="Время работы"
              fullWidth
              value={newPoint.working_hours}
              onChange={(e) => setNewPoint({ ...newPoint, working_hours: e.target.value })}
              placeholder="Например: Пн-Пт 9:00-18:00"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Описание"
              fullWidth
              multiline
              rows={3}
              value={newPoint.description}
              onChange={(e) => setNewPoint({ ...newPoint, description: e.target.value })}
              placeholder="Дополнительная информация о точке аренды"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="timezone-select-label">Часовой пояс</InputLabel>
              <Select
                labelId="timezone-select-label"
                id="timezone-select"
                value={newPoint.timezone}
                label="Часовой пояс"
                onChange={(e) => setNewPoint({ ...newPoint, timezone: e.target.value })}
              >
                {Object.entries(TIMEZONES).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Отмена
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading || !newPoint.name.trim()}
          sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
        >
          {loading ? 'Создание...' : 'Создать точку аренды'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRentalPointDialog;
